/**
 * SLAPerformanceCard — the agent's daily SLA performance widget.
 *
 * Five KPI tiles (Assigned / Attended / Missed / Compliance % / Active
 * Timers) plus, when there's missed activity in the window, a red
 * highlight panel listing the most recent missed leads with their
 * overdue duration.
 *
 * Period filter: Today / Yesterday / This Week / This Month.
 * Data source: GET /api/dashboard/sla (built in Phase 3).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Skeleton } from './ui/skeleton';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight, Clock,
  RefreshCw, UserMinus, UserPlus,
} from 'lucide-react';
import HelpInfo from './HelpInfo';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
];

const fmtOverdue = (seconds) => {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m overdue`;
  return `${m}m overdue`;
};

const fmtTimestamp = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function SLAPerformanceCard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/dashboard/sla`, {
        params: { token, period },
      });
      setData(res.data?.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis;
  const recentMissed = data?.recent_missed || [];

  const compliance = kpis?.compliance_pct ?? 100;
  const complianceTone = useMemo(() => {
    if (compliance >= 90) return 'text-emerald-600';
    if (compliance >= 70) return 'text-amber-600';
    return 'text-red-600';
  }, [compliance]);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Activity size={16} className="text-violet-500" />
          SLA Performance
          <HelpInfo
            align="inline"
            title="How SLA Performance is measured"
            steps={[
              <><strong>Assigned</strong> — leads that landed in your name during the window.</>,
              <><strong>Attended</strong> — leads where you logged a real action (call, WhatsApp, SMS or email) within the SLA window. Status changes and notes don't count.</>,
              <><strong>Missed</strong> — leads that auto-rotated to someone else because the SLA window expired with no action from you.</>,
              <><strong>Compliance %</strong> = Attended ÷ (Attended + Missed). Aim above 90%.</>,
              <><strong>Active timers</strong> are SLA windows running right now in your name. Clear them before they breach.</>,
            ]}
            tip="The fastest fix for a low score is a call or WhatsApp in the first minute after assignment — that single action attends the lead and stops the rotation timer."
          />
          <span className="ml-auto flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiTile loading={loading && !kpis} icon={UserPlus} tone="text-blue-500"
                    label="Assigned" value={kpis?.assigned ?? 0} />
          <KpiTile loading={loading && !kpis} icon={CheckCircle2} tone="text-emerald-500"
                    label="Attended" value={kpis?.attended ?? 0} />
          <KpiTile loading={loading && !kpis} icon={UserMinus} tone="text-red-500"
                    label="Missed" value={kpis?.missed ?? 0} />
          <KpiTile loading={loading && !kpis} icon={Activity}
                    valueClass={complianceTone}
                    label="Compliance" value={`${compliance}%`} />
          <KpiTile loading={loading && !kpis} icon={Clock} tone="text-amber-500"
                    label="Active timers" value={kpis?.active_timers ?? 0} />
        </div>

        {/* Recently missed — red highlight panel per spec */}
        {recentMissed.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-semibold text-red-700 flex items-center gap-1.5 mb-2">
              <AlertTriangle size={12} />
              Recently missed
            </p>
            <ul className="space-y-1.5">
              {recentMissed.map((m) => (
                <li key={`${m.lead_id}-${m.missed_at}`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/contacts/${m.lead_id}`)}
                    className="w-full text-left text-xs flex items-center justify-between gap-2 hover:bg-red-100 rounded px-1.5 py-1 transition"
                  >
                    <span className="flex-1 min-w-0 truncate text-red-900">
                      <span className="font-medium">{m.lead_name}</span>
                      <span className="text-red-600 ml-1.5">— {fmtTimestamp(m.missed_at)}</span>
                    </span>
                    <Badge className="text-[10px] bg-red-100 text-red-700 border border-red-300">
                      {fmtOverdue(m.overdue_seconds)}
                    </Badge>
                    <ChevronRight size={12} className="text-red-400 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && (!kpis || kpis.assigned === 0) && (
          <p className="text-xs text-slate-400 italic text-center py-2">
            No SLA-tracked leads in this window — the counter resets when leads start landing.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const KpiTile = ({ icon: Icon, tone, label, value, valueClass, loading }) => (
  <div className="rounded-md border border-slate-200 p-2.5">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
      {Icon && <Icon size={11} className={tone} />}
      {label}
    </div>
    {loading ? (
      <Skeleton className="h-6 w-12 mt-1" />
    ) : (
      <div className={`text-xl font-bold mt-0.5 ${valueClass || 'text-slate-900'}`}>
        {value}
      </div>
    )}
  </div>
);
